import { useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { AircallAnbindungLeitungen, ApiClient } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { formatDatum } from "../format.js";
import "./AircallLeitungModal.css";

/**
 * LEITUNG ZUORDNEN bzw. ZUORDNUNG BEENDEN (Spec „Nacharbeitszeit, Leitung und Kunde je Anruf", Fassung 1.3, V5, E311,
 * E313, F12–F17) — EIN striktes Modal für die Kundenakte und die Klärliste im Dienst-Detail. Vor dem Schreiben fragt
 * die kleine gestapelte Bestätigung und nennt die Auswirkung: wie viele gespeicherte Anrufe ihren Abrechnungskunden
 * wechseln (rückwirkend erlaubt, E313). Die Version ist die der Anbindung; eine 409 heißt „inzwischen geändert".
 * Block `alm`.
 */

export type AircallLeitungClient = Pick<ApiClient, "ordneAircallLeitungZu" | "beendeAircallLeitung"
  | "aircallAuswirkung">;

export type LeitungModalProps = {
  client: AircallLeitungClient;
  customerId: string;
  /** Der Name des Kunden für die Bestätigung; `null` = „diesem Kunden". */
  kundeName: string | null;
  sicht: AircallAnbindungLeitungen;
  modus: "ZUORDNEN" | "BEENDEN";
  /** Vorgewählte Leitung (Beenden: Pflicht; Zuordnen aus der Klärliste: die offene Leitung). */
  leitungId?: string;
  onFertig: (sicht: AircallAnbindungLeitungen) => void;
  onAbbrechen: () => void;
  /** 409: Der Aufrufer lädt die Sicht neu (neue Version); das Modal bleibt mit den Eingaben offen. */
  onKonflikt?: (() => void) | undefined;
};

/** „Service Strom — +49 35752 943257-10" (Name, sonst Nummer). */
export function leitungText(l: { name: string | null; digits: string | null }): string {
  if (l.name !== null && l.digits !== null) return `${l.name} — ${l.digits}`;
  return l.name ?? l.digits ?? "Leitung ohne Namen";
}

const BISHER: Record<AircallAnbindungLeitungen["auswahl"][number]["heute"], string> = {
  DIESE: "dieser Kunde", ANDERE: "einem anderen Kunden", KEINE: "keinem Kunden", OFFEN: "nicht zugeordnet",
};

/** „312 gespeicherte Anrufe" / „1 gespeicherter Anruf" — der Zähler jeder Bestätigung (E313). */
export function anrufeText(n: number): string {
  return `${n} gespeicherte${n === 1 ? "r Anruf" : " Anrufe"}`;
}

type Feldfehler = { leitungId?: string; validFrom?: string; note?: string };
const MODAL_FELDER: readonly string[] = ["leitungId", "validFrom", "note"];

/**
 * Die Formprüfung von „Gültig ab" VOR jeder Anfrage — dieselbe Regel und dieselben Texte wie `gueltigAbPruefen` der
 * Domäne (F12/F13; `web` darf `domain` nicht importieren): ein Tag JJJJ-MM-TT, nicht vor dem 01.01.2026. Ein leeres
 * Feld ginge sonst als 400 ohne Feldbezug zurück. Den unmöglichen Tag (2026-02-30) meldet der Server mit 422 am Feld.
 */
export function gueltigAbBefund(tag: string): string | null {
  if (tag === "") return "Bitte ein Datum angeben.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(tag)) return "„Gültig ab“ ist kein Datum in der Form JJJJ-MM-TT.";
  if (tag < "2026-01-01") return "„Gültig ab“ darf nicht vor dem 01.01.2026 liegen.";
  return null;
}

export function LeitungModal(p: LeitungModalProps): ReactElement {
  const { client, customerId, sicht, modus } = p;
  const [leitungId, setLeitungId] = useState(p.leitungId ?? "");
  const [validFrom, setValidFrom] = useState(sicht.heute);
  const [note, setNote] = useState("");
  const [fehler, setFehler] = useState<Feldfehler>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const { bestaetigen } = useBestaetigung();
  const gewaehlt = sicht.auswahl.find((a) => a.leitungId === leitungId) ?? null;
  const geaendert = leitungId !== (p.leitungId ?? "") || validFrom !== sicht.heute || note !== "";

  async function speichern(): Promise<void> {
    if (laeuft) return;
    if (gewaehlt === null) { setFehler({ leitungId: "Bitte eine Leitung wählen." }); return; }
    const befund = gueltigAbBefund(validFrom);
    if (befund !== null) { setFehler({ validFrom: befund }); return; }
    setLaeuft(true);
    setMeldung(null);
    setFehler({});
    try {
      const { anrufe } = await client.aircallAuswirkung(customerId, leitungId,
        { validFrom, ende: modus === "BEENDEN" });
      const leitung = gewaehlt.name ?? gewaehlt.digits ?? "ohne Namen";
      const ab = formatDatum(validFrom);
      const frage = modus === "ZUORDNEN"
        ? { titel: "Leitung zuordnen?", bestaetigenText: "Zuordnen",
            text: `Leitung ${leitung} ab ${ab} ${p.kundeName ?? "diesem Kunden"} zuordnen? `
              + `Bisher: ${gewaehlt.heute === "ANDERE" ? gewaehlt.kundeName ?? BISHER.ANDERE : BISHER[gewaehlt.heute]}. `
              + `Betroffen: ${anrufeText(anrufe)}.` }
        : { titel: "Zuordnung beenden?", bestaetigenText: "Beenden",
            text: `Leitung ${leitung} ab ${ab} keinem Kunden zuordnen? Betroffen: ${anrufeText(anrufe)}.` };
      if (!(await bestaetigen(frage))) return;
      const neu = modus === "ZUORDNEN"
        ? await client.ordneAircallLeitungZu(customerId,
          { version: sicht.version, leitungId, validFrom, note: note.trim() === "" ? null : note.trim() })
        : await client.beendeAircallLeitung(customerId, leitungId, { version: sicht.version, validFrom });
      p.onFertig(neu);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setMeldung("Die Aircall-Anbindung wurde inzwischen geändert — der Stand wird neu geladen; "
          + "bitte prüfen und erneut zuordnen bzw. beenden.");
        p.onKonflikt?.();
      } else if (e instanceof ApiError && e.status === 422 && (e.fields ?? []).length > 0) {
        const felder = e.fields ?? [];
        setFehler(Object.fromEntries(felder.filter((f) => MODAL_FELDER.includes(f.field))
          .map((f) => [f.field, f.message])) as Feldfehler);
        // Ein Befund zu einem Feld, das das Modal nicht zeigt (z. B. `accountId`), verschwindet nicht still.
        const sonst = felder.filter((f) => !MODAL_FELDER.includes(f.field)).map((f) => f.message);
        if (sonst.length > 0) setMeldung(sonst.join(" "));
      } else {
        setMeldung(e instanceof ApiError ? e.message : "Die Zuordnung konnte nicht gespeichert werden.");
      }
    } finally {
      setLaeuft(false);
    }
  }

  async function abbrechen(): Promise<void> {
    if (laeuft) return;
    if (await darfAbbrechen(geaendert)) p.onAbbrechen();
  }

  return (
    <Modal titel={modus === "ZUORDNEN" ? "Leitung zuordnen" : "Zuordnung beenden"}
      onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={laeuft}>
      {meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}
      <Formular beschriftung="oben">
        <div className="alm">
          <Auswahl beschriftung="Leitung" wert={leitungId} gesperrt={laeuft || modus === "BEENDEN"}
            {...(fehler.leitungId === undefined ? {} : { fehler: fehler.leitungId })}
            onGeaendert={(d) => { setLeitungId(d.wert); setFehler({}); }}>
            <option value="">Leitung wählen …</option>
            {sicht.auswahl.map((a) => (
              <option key={a.leitungId} value={a.leitungId} disabled={modus === "ZUORDNEN" && !a.aktiv}>
                {leitungText(a)}
                {a.heute === "ANDERE" ? ` (gehört ${a.kundeName ?? "einem anderen Kunden"})` : ""}
                {a.heute === "DIESE" ? " (dieser Kunde)" : ""}
                {a.aktiv ? "" : " (nicht mehr bei Aircall)"}
              </option>
            ))}
          </Auswahl>
          <Feld id="alm-ab" label={modus === "ZUORDNEN" ? "Gültig ab" : "Ab"} typ="date" bearbeitbar
            wert={validFrom} onChange={(w) => { setValidFrom(w); setFehler({}); }} gesperrt={laeuft}
            {...(fehler.validFrom === undefined ? {} : { fehler: fehler.validFrom })} />
          {modus === "ZUORDNEN" && (
            <Feld id="alm-notiz" label="Notiz (optional)" bearbeitbar wert={note} onChange={setNote} max={200}
              gesperrt={laeuft} {...(fehler.note === undefined ? {} : { fehler: fehler.note })} />
          )}
        </div>
      </Formular>
      <ModalFuss speichernText={modus === "ZUORDNEN" ? "Zuordnen" : "Beenden"}
        speichernGesperrt={laeuft || gewaehlt === null} abbrechenGesperrt={laeuft}
        onSpeichern={() => { void speichern(); }} onAbbrechen={() => { void abbrechen(); }} />
    </Modal>
  );
}
